import { useCallback } from 'react';
import usePersistentState from '@/hooks/common/usePersistentState';

const useCampaignPersistence = (selectedRegion, selectedBrand) => {
  // Generate a unique storage key based on region and brand
  const storageKey = `launchCampaign_${selectedRegion}_${selectedBrand}`;

  // Initial state structure for campaign
  const initialState = {
    expandedStep: 1,
    completedSteps: [],
    campaignData: {},
    selectedPOCs: [],
    creativeData: {}
  };

  // Use the base persistent state hook
  const {
    state,
    updateState,
    clearPersistedData,
    hasPersistedData,
    hasDataToConfirm,
    getRemainingTime
  } = usePersistentState(storageKey, initialState);

  // Extract state values for easier access with safe defaults
  const {
    expandedStep = 1,
    completedSteps = [],
    campaignData = {},
    selectedPOCs = [],
    creativeData = {}
  } = state;

  // State update helpers with validation
  const setExpandedStep = useCallback((step) => {
    if (step === null || (typeof step === 'number' && step >= 1 && step <= 3)) {
      updateState({ expandedStep: step });
    }
  }, [updateState]);

  const setCompletedSteps = useCallback((steps) => {
    if (Array.isArray(steps)) {
      updateState({ completedSteps: steps });
    }
  }, [updateState]);

  const addCompletedStep = useCallback((stepNumber) => {
    const steps = completedSteps || [];
    if (!steps.includes(stepNumber)) {
      updateState({ completedSteps: [...steps, stepNumber] });
    }
  }, [completedSteps, updateState]);

  const setCampaignData = useCallback((data) => {
    if (typeof data === 'function') {
      // Handle functional updates like prev => ({ ...prev, newField: value })
      updateState(prevState => {
        const newCampaignData = data(prevState.campaignData);
        return { 
          campaignData: newCampaignData 
        };
      });
    } else if (typeof data === 'object' && data !== null) {
      // Handle direct object updates
      updateState({ campaignData: data });
    }
  }, [updateState]);

  const setSelectedPOCs = useCallback((pocs) => {
    if (Array.isArray(pocs)) {
      updateState({ selectedPOCs: pocs });
    }
  }, [updateState]);

  const setCreativeData = useCallback((data) => {
    if (typeof data === 'object' && data !== null) {
      // Don't persist creative files at all
      const serializableData = { ...data };
      
      // Remove creative files from persistence
      delete serializableData.creatives;
      delete serializableData.creative;
      
      updateState({ creativeData: serializableData });
    }
  }, [updateState]);

  // Campaign-specific actions
  const toggleStep = useCallback((stepNumber) => {
    if (expandedStep === stepNumber) {
      setExpandedStep(null);
    } else {
      setExpandedStep(stepNumber);
    }
  }, [expandedStep, setExpandedStep]);

  const completeStep = useCallback((stepNumber, data) => {
    addCompletedStep(stepNumber);

    if (stepNumber === 1 && data) {
      setCampaignData(prev => ({ ...prev, ...data }));
    } else if (stepNumber === 2 && data) {
      setSelectedPOCs(data);
    } else if (stepNumber === 3 && data) {
      setCreativeData(data);
    }
  }, [addCompletedStep, setCampaignData, setSelectedPOCs, setCreativeData]);

  const canProceedToStep = useCallback((stepNumber) => {
    const steps = completedSteps || [];
    const hasCampaignId = campaignData && campaignData.campaignId;
    
    if (stepNumber === 1) return true;
    // Step 2 can be accessed if Step 1 is completed OR if we have a campaign ID (indicating successful creation)
    if (stepNumber === 2) {
      return Boolean((steps.includes(1) || hasCampaignId) && selectedRegion && selectedBrand);
    }
    if (stepNumber === 3) return steps.includes(2);
    return false;
  }, [completedSteps, campaignData, selectedRegion, selectedBrand]);

  // Helper function to ensure step 1 is marked complete if campaign exists
  const ensureStep1Completed = useCallback(() => {
    const steps = completedSteps || [];
    const hasCampaignId = campaignData && campaignData.campaignId;
    
    if (hasCampaignId && !steps.includes(1)) {
      addCompletedStep(1);
    }
  }, [completedSteps, campaignData, addCompletedStep]);

  const resetCampaign = useCallback(() => {
    console.log('Resetting campaign data');
    // Reset all state to initial values
    updateState(initialState);
    // Clear from localStorage
    clearPersistedData();
  }, [clearPersistedData, updateState, initialState]);

  // Helper function to get formatted remaining time
  const getFormattedRemainingTime = useCallback(() => {
    const remainingMs = getRemainingTime();
    if (remainingMs <= 0) return 'Expired';
    
    const totalMinutes = Math.floor(remainingMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    } else {
      return `${minutes}m remaining`;
    }
  }, [getRemainingTime]);

  return {
    // State
    expandedStep,
    completedSteps,
    campaignData,
    selectedPOCs,
    creativeData,
    hasPersistedData,
    hasDataToConfirm,

    // Actions
    setExpandedStep,
    setCompletedSteps,
    addCompletedStep,
    setCampaignData,
    setSelectedPOCs,
    setCreativeData,
    toggleStep,
    completeStep,
    canProceedToStep,
    ensureStep1Completed,
    resetCampaign,

    // Time utilities
    getRemainingTime,
    getFormattedRemainingTime,

    // Utilities
    clearPersistedData,
    updateState
  };
};

export default useCampaignPersistence;
